/**
 * Change Tracking for Incremental Saves
 * Tracks which pages have been modified to enable selective saves
 */

class ChangeTracker {
  constructor() {
    this.dirtyPages = new Set();
    this.pageSnapshots = new Map();
    this.changeHistory = [];
  }

  /**
   * Mark a page as dirty (modified)
   * @param {string} pageId - Page ID
   */
  markDirty(pageId) {
    this.dirtyPages.add(pageId);
    this.changeHistory.push({
      pageId,
      timestamp: Date.now(),
      action: 'modified'
    });
    
    // Keep only last 1000 changes
    if (this.changeHistory.length > 1000) {
      this.changeHistory.shift();
    }
    
    console.log(`📝 Page marked as dirty: ${pageId}`);
    console.log(`📝 Total dirty pages: ${this.dirtyPages.size}`, Array.from(this.dirtyPages));
  }

  /**
   * Mark a page as clean (saved)
   * @param {string} pageId - Page ID
   */
  markClean(pageId) {
    this.dirtyPages.delete(pageId);
    console.log(`✅ Page marked as clean: ${pageId}`);
  }

  /**
   * Check if a page is dirty
   * @param {string} pageId - Page ID
   * @returns {boolean} - Is dirty
   */
  isDirty(pageId) {
    return this.dirtyPages.has(pageId);
  }

  /**
   * Get all dirty pages
   * @returns {Array<string>} - Dirty page IDs
   */
  getDirtyPages() {
    return Array.from(this.dirtyPages);
  }

  /**
   * Get dirty page count
   * @returns {number} - Count
   */
  getDirtyCount() {
    return this.dirtyPages.size;
  }

  /**
   * Clear all dirty flags
   */
  clearAll() {
    this.dirtyPages.clear();
    console.log('🧹 All pages marked as clean');
  }

  /**
   * Clean up invalid page IDs (non-ObjectId format)
   */
  cleanupInvalidIds() {
    const validObjectIdRegex = /^[0-9a-fA-F]{24}$/;
    const invalidIds = [];
    
    for (const pageId of this.dirtyPages) {
      if (!validObjectIdRegex.test(pageId)) {
        invalidIds.push(pageId);
      }
    }
    
    invalidIds.forEach(id => {
      this.dirtyPages.delete(id);
      this.pageSnapshots.delete(id);
      console.log(`🧹 Cleaned up invalid page ID: ${id}`);
    });
    
    if (invalidIds.length > 0) {
      console.log(`🧹 Cleaned up ${invalidIds.length} invalid page IDs`);
    }
  }

  /**
   * Take snapshot of page data
   * @param {string} pageId - Page ID
   * @param {Object} data - Page data
   */
  snapshot(pageId, data) {
    this.pageSnapshots.set(pageId, {
      data: JSON.parse(JSON.stringify(data)),
      timestamp: Date.now()
    });
  }

  /**
   * Check if page has changed since snapshot
   * @param {string} pageId - Page ID
   * @param {Object} currentData - Current page data
   * @returns {boolean} - Has changed
   */
  hasChanged(pageId, currentData) {
    const snapshot = this.pageSnapshots.get(pageId);
    if (!snapshot) return true;
    
    const currentJson = JSON.stringify(currentData);
    const snapshotJson = JSON.stringify(snapshot.data);
    
    return currentJson !== snapshotJson;
  }

  /**
   * Check if page has a snapshot
   * @param {string} pageId - Page ID
   * @returns {boolean} - Has snapshot
   */
  hasSnapshot(pageId) {
    return this.pageSnapshots.has(pageId);
  }

  /**
   * Get change statistics
   * @returns {Object} - Stats
   */
  getStats() {
    return {
      dirtyPages: this.dirtyPages.size,
      totalSnapshots: this.pageSnapshots.size,
      recentChanges: this.changeHistory.slice(-10),
      oldestChange: this.changeHistory[0]?.timestamp || null
    };
  }

  /**
   * Get changes since timestamp
   * @param {number} since - Timestamp
   * @returns {Array} - Changes
   */
  getChangesSince(since) {
    return this.changeHistory.filter(change => change.timestamp > since);
  }

  /**
   * Debug method to check current state
   * @returns {Object} - Debug info
   */
  debugState() {
    return {
      dirtyPages: Array.from(this.dirtyPages),
      dirtyCount: this.dirtyPages.size,
      snapshots: Array.from(this.pageSnapshots.keys()),
      snapshotCount: this.pageSnapshots.size,
      recentChanges: this.changeHistory.slice(-5)
    };
  }
}

// Create singleton instance
const changeTracker = new ChangeTracker();

// Expose debug function globally for testing
if (typeof window !== 'undefined') {
  window.debugChangeTracker = () => {
    const state = changeTracker.debugState();
    console.log('🔍 Change Tracker Debug State:', state);
    return state;
  };
}

export default changeTracker;

/**
 * Setup change tracking for GrapesJS editor
 * @param {Object} editor - GrapesJS editor instance
 */
export function setupChangeTracking(editor) {
  // Track component changes
  editor.on('component:add', (component) => {
    try {
      const page = editor.Pages && editor.Pages.getSelected ? editor.Pages.getSelected() : null;
      if (page && typeof page.get === 'function') {
        const pageId = page.get('id');
        console.log('🔍 Component added - Page ID:', pageId, 'Component:', component);
        if (pageId) {
          changeTracker.markDirty(pageId);
        }
      }
    } catch (error) {
      console.error('❌ Error in component:add event:', error);
    }
  });

  editor.on('component:remove', (component) => {
    try {
      const page = editor.Pages && editor.Pages.getSelected ? editor.Pages.getSelected() : null;
      if (page && typeof page.get === 'function') {
        const pageId = page.get('id');
        if (pageId) {
          changeTracker.markDirty(pageId);
        }
      }
    } catch (error) {
      console.error('❌ Error in component:remove event:', error);
    }
  });

  editor.on('component:update', (component) => {
    try {
      const page = editor.Pages && editor.Pages.getSelected ? editor.Pages.getSelected() : null;
      if (page && typeof page.get === 'function') {
        const pageId = page.get('id');
        if (pageId) {
          changeTracker.markDirty(pageId);
        }
      }
    } catch (error) {
      console.error('❌ Error in component:update event:', error);
    }
  });

  // Track style changes
  editor.on('style:update', () => {
    try {
      const page = editor.Pages && editor.Pages.getSelected ? editor.Pages.getSelected() : null;
      if (page && typeof page.get === 'function') {
        const pageId = page.get('id');
        if (pageId) {
          changeTracker.markDirty(pageId);
        }
      }
    } catch (error) {
      console.error('❌ Error in style:update event:', error);
    }
  });

  // Take snapshot after save
  editor.on('storage:end:store', () => {
    const pages = editor.Pages.getAll();
    pages.forEach(page => {
      const pageId = page.get('id');
      const data = {
        components: page.getMainComponent().toJSON(),
        styles: editor.getCss({ component: page.getMainComponent() })
      };
      changeTracker.snapshot(pageId, data);
      changeTracker.markClean(pageId);
    });
  });

  console.log('✅ Change tracking initialized');
}

/**
 * Filter pages to only dirty ones
 * @param {Array} pages - All pages
 * @returns {Array} - Only dirty pages
 */
export function filterDirtyPages(pages) {
  return pages.filter(page => changeTracker.isDirty(page.id));
}

/**
 * Setup comprehensive storage event handling for GrapesJS editor
 * Implements proper storage event handling following GrapesJS documentation
 * @param {Object} editor - GrapesJS editor instance
 */
export function setupStorageEvents(editor) {
  console.log('🔧 Setting up GrapesJS storage events...');

  // Storage request start
  editor.on('storage:start', (type) => {
    console.log('📦 Storage request started:', type);
    window.saveStartTime = performance.now(); // Track save timing
    if (window.updateSaveStatus) {
      window.updateSaveStatus('saving');
    }
  });

  // Storage store request start
  editor.on('storage:start:store', (data) => {
    console.log('💾 Storage store request started');
    console.log('📊 Data to store:', {
      pages: data.pages?.length || 0,
      assets: data.assets?.length || 0,
      styles: data.styles?.length || 0
    });
  });

  // Storage load request start
  editor.on('storage:start:load', () => {
    console.log('📥 Storage load request started');
    // Note: 'loading' is not a valid status, use 'saving' which indicates activity
    if (window.updateSaveStatus) {
      window.updateSaveStatus('saving');
    }
  });

  // Storage loaded successfully
  editor.on('storage:load', (data, res) => {
    console.log('✅ Storage loaded successfully');
    console.log('📊 Loaded data:', {
      pages: data.pages?.length || 0,
      assets: data.assets?.length || 0,
      styles: data.styles?.length || 0
    });
    
    if (window.updateSaveStatus) {
      window.updateSaveStatus('saved'); // Use 'saved' instead of 'loaded' (not a valid status)
    }

    if (window.showNotification) {
      window.showNotification('Project loaded successfully', 'success');
    }
  });

  // Storage stored successfully
  editor.on('storage:store', (data, res) => {
    console.log('✅ Storage stored successfully');
    console.log('📊 Stored data:', {
      pages: data.pages?.length || 0,
      assets: data.assets?.length || 0,
      styles: data.styles?.length || 0
    });
    
    if (window.updateSaveStatus) {
      window.updateSaveStatus('saved');
    }
    
    if (window.showNotification) {
      window.showNotification('Changes saved successfully', 'success');
    }
    
    // Track successful save metrics
    if (window.saveMetrics) {
      window.saveMetrics.successfulSaves++;
      const saveTime = performance.now() - (window.saveStartTime || 0);
      window.saveMetrics.saveTimes.push(saveTime);
      window.saveMetrics.averageSaveTime = 
        window.saveMetrics.saveTimes.reduce((a, b) => a + b, 0) / window.saveMetrics.saveTimes.length;
    }
  });

  // Storage request completed (after load/store)
  editor.on('storage:after', (type) => {
    console.log('🏁 Storage request completed:', type);
  });

  // Storage request ended (including errors)
  editor.on('storage:end', (type) => {
    console.log('🔚 Storage request ended:', type);
    // Don't update status here - let storage:store or storage:load set the final status
    // Setting 'idle' here would override the 'saved' status set by storage:store
  });

  // Storage store request ended
  editor.on('storage:end:store', () => {
    console.log('💾 Storage store request ended');
  });

  // Storage load request ended
  editor.on('storage:end:load', () => {
    console.log('📥 Storage load request ended');
  });

  // Storage error handling - CRITICAL: Use proper Error objects
  // NOTE: Do NOT re-throw errors here - GrapesJS handles errors internally
  // Re-throwing causes "PromiseReject called on non-object" errors
  editor.on('storage:error', (err, type) => {
    console.error('❌ Storage error:', err, 'Type:', type);

    if (window.updateSaveStatus) {
      window.updateSaveStatus('error');
    }

    // Track failed save metrics
    if (window.saveMetrics) {
      window.saveMetrics.failedSaves++;
    }

    // Handle different error types with proper Error objects
    let errorMessage = 'Storage operation failed';
    let errorType = 'error';

    // Create proper Error object if we received a string or other type
    let properError = err;
    if (typeof err === 'string') {
      properError = createStorageError(err);
    } else if (err && typeof err === 'object' && !err.message) {
      properError = createStorageError('Storage operation failed', err.status || 500, err);
    }

    if (properError && typeof properError === 'object') {
      if (properError.status === 401) {
        errorMessage = 'Authentication failed. Please refresh and log in again.';
        errorType = 'warning';
      } else if (properError.status === 409) {
        errorMessage = 'Save conflict detected. Please reload to get latest changes.';
        errorType = 'warning';
      } else if (properError.status >= 500) {
        errorMessage = 'Server error. Your changes will be retried automatically.';
        errorType = 'error';
      } else if (properError.message) {
        errorMessage = properError.message;
      }
    }

    if (window.showNotification) {
      window.showNotification(errorMessage, errorType);
    }

    // NOTE: Do NOT re-throw - GrapesJS handles the error internally
    // Throwing here causes "PromiseReject called on non-object" errors
  });

  // Storage store error - CRITICAL: Handle with proper Error objects
  // NOTE: Do NOT re-throw errors here - GrapesJS handles errors internally
  editor.on('storage:error:store', (err) => {
    console.error('❌ Storage store error:', err);

    // Create proper Error object
    let properError = err;
    if (typeof err === 'string') {
      properError = createStorageError(err);
    }

    // Specific handling for store errors
    if (properError && properError.status === 401) {
      console.log('🔄 Attempting to refresh authentication for store error...');

      // Try to refresh token
      if (window.authContext?.fetchUserProfile) {
        window.authContext.fetchUserProfile().then(() => {
          console.log('✅ Authentication refreshed, you can try saving again');
          if (window.showNotification) {
            window.showNotification('Authentication refreshed. Please try saving again.', 'info');
          }
        }).catch((refreshError) => {
          console.error('❌ Authentication refresh failed:', refreshError);
        });
      }
    }

    // NOTE: Do NOT re-throw - GrapesJS handles the error internally
  });

  // Storage load error - CRITICAL: Handle with proper Error objects
  // NOTE: Do NOT re-throw errors here - GrapesJS handles errors internally
  editor.on('storage:error:load', (err) => {
    console.error('❌ Storage load error:', err);

    // Create proper Error object
    let properError = err;
    if (typeof err === 'string') {
      properError = createStorageError(err);
    }

    let errorMessage = 'Failed to load project data';
    if (properError && properError.status === 401) {
      errorMessage = 'Authentication required. Please refresh and log in.';
    } else if (properError && properError.status === 404) {
      errorMessage = 'Project not found. Starting with empty project.';
    }

    if (window.showNotification) {
      window.showNotification(errorMessage, 'error');
    }

    // NOTE: Do NOT re-throw - GrapesJS handles the error internally
  });

  console.log('✅ Storage events initialized');
}

/**
 * Setup native GrapesJS page events following the documentation
 * @param {Object} editor - GrapesJS editor instance
 */
export function setupPageEvents(editor) {
  console.log('📄 Setting up native GrapesJS page events...');
  
  // Clean up any invalid page IDs from previous sessions
  changeTracker.cleanupInvalidIds();

  // Helper to safely trigger a save without overlapping with an in-progress save
  const safeSave = (delay = 500) => {
    setTimeout(() => {
      // Deal-redirect prefill (TemplateBuilder.jsx's runDealPrefill) sets
      // this while it's programmatically creating/populating a page --
      // those Pages.add()/select() calls fire the exact same page:add /
      // page:select events a real user edit would, which was causing an
      // autosave to fire within ~1-2s of the editor mounting, before the
      // user had touched anything. Prefill does its own single deliberate
      // store() once it's actually done, so every save queued while it's
      // running would just be redundant on top of that anyway.
      if (window._gjsPrefillInProgress) {
        console.log('⏳ Deal prefill in progress - deferring to its own save on completion');
        return;
      }
      if (!window._gjsSaveInProgress) {
        editor.store().catch((err) => {
          console.log('⚠️ Save deferred - will be saved on next autosave');
        });
      } else {
        console.log('⏳ Save already in progress - will be saved on next autosave');
      }
    }, delay);
  };

  // page:add - Added new page
  editor.on('page:add', (page) => {
    try {
      const pageName = (page && typeof page.getName === 'function') ? page.getName() : 'Unknown';
      const pageId = (page && typeof page.getId === 'function') ? page.getId() : null;
      console.log('📄 Page added:', pageName, 'ID:', pageId);

      // Track the new page for changes
      if (pageId) {
        changeTracker.markClean(pageId);
      }

      // Note: Don't set status to 'modified' - it's not a valid status
      // The autosave triggered below will set status to 'saving' then 'saved'

      // Trigger auto-save after page addition (using safe save to prevent overlaps)
      safeSave(500);
    } catch (error) {
      console.error('❌ Error in page:add event:', error);
    }
  });

  // page:remove - Page removed
  editor.on('page:remove', (page) => {
    try {
      const pageName = (page && typeof page.getName === 'function') ? page.getName() : 'Unknown';
      const pageId = (page && typeof page.getId === 'function') ? page.getId() : null;
      console.log('📄 Page removed:', pageName, 'ID:', pageId);

      // Clean up tracking for removed page
      if (pageId) {
        changeTracker.markClean(pageId);
      }

      // Note: Don't set status to 'modified' - it's not a valid status
      // The autosave triggered below will set status to 'saving' then 'saved'

      // Trigger auto-save after page removal (using safe save to prevent overlaps)
      safeSave(500);
    } catch (error) {
      console.error('❌ Error in page:remove event:', error);
    }
  });

  // page:select - New page selected
  editor.on('page:select', (page, previousPage) => {
    try {
      const currentPageName = page ? ((typeof page.getName === 'function' ? page.getName() : null) || (typeof page.getId === 'function' ? page.getId() : null)) : 'none';
      const previousPageName = previousPage ? ((typeof previousPage.getName === 'function' ? previousPage.getName() : null) || (typeof previousPage.getId === 'function' ? previousPage.getId() : null)) : 'none';

      console.log('📄 Page selected:', currentPageName, '(previous:', previousPageName + ')');

      // Mark previous page as clean if it was being edited
      if (previousPage && typeof previousPage.getId === 'function') {
        const prevPageId = previousPage.getId();
        if (prevPageId && changeTracker.isDirty(prevPageId)) {
          console.log('📄 Auto-saving previous page before switching');
          // Trigger save for previous page (using safe save to prevent overlaps)
          safeSave(100);
        }
      }

      // Update current page tracking
      if (page && typeof page.getId === 'function') {
        const pageId = page.getId();
        if (pageId) {
          // Initialize tracking for new page if not already tracked
          if (!changeTracker.hasSnapshot(pageId)) {
            const data = {
              components: (page && typeof page.getMainComponent === 'function') ? page.getMainComponent().toJSON() : {},
              styles: editor.getCss()
            };
            changeTracker.snapshot(pageId, data);
            changeTracker.markClean(pageId);
          }
        }
      }

      // Note: Don't change status when switching pages - let the save status reflect actual save state
    } catch (error) {
      console.error('❌ Error in page:select event:', error);
    }
  });

  // page:update - Page updated
  editor.on('page:update', (page, changes) => {
    try {
      const pageName = (page && typeof page.getName === 'function') ? page.getName() : 'Unknown';
      const pageId = (page && typeof page.getId === 'function') ? page.getId() : null;
      console.log('📄 Page updated:', pageName, 'ID:', pageId, 'Changes:', changes ? Object.keys(changes) : 'none');
      
      // Mark page as dirty when updated
      if (pageId) {
        changeTracker.markDirty(pageId);
      }

      // Note: Don't set status to 'modified' - it's not a valid status
      // The autosave will handle the status updates (saving -> saved)
    } catch (error) {
      console.error('❌ Error in page:update event:', error);
    }
  });

  // page - Catch-all event for all page events
  editor.on('page', ({ event, model, ...data }) => {
    try {
      const modelName = (model && typeof model.getName === 'function') ? model.getName() : 
                       (model && typeof model.getId === 'function') ? model.getId() : 'none';
      console.log('📄 Page event:', event, 'Model:', modelName, 'Data:', data);
      
      // Additional logging for debugging
      if (event === 'add' || event === 'remove' || event === 'select' || event === 'update') {
        console.log('📄 Catch-all page event details:', {
          event,
          modelId: (model && typeof model.getId === 'function') ? model.getId() : null,
          modelName: (model && typeof model.getName === 'function') ? model.getName() : null,
          additionalData: data
        });
      }
    } catch (error) {
      console.error('❌ Error in page catch-all event:', error);
    }
  });

  console.log('✅ Page events initialized');
}

/**
 * Create a proper Error object for storage operations
 * Following GrapesJS documentation best practices
 * @param {string} message - Error message
 * @param {number} status - HTTP status code
 * @param {Object} details - Additional error details
 * @returns {Error} Proper Error object
 */
export function createStorageError(message, status = 500, details = {}) {
  const error = new Error(message);
  error.status = status;
  error.details = details;
  error.timestamp = new Date().toISOString();
  return error;
}
